import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Tesseract from 'tesseract.js';
import {
  preprocessCanvas,
  recommendOcrScale,
  toStructured,
  renderMarkdown
} from './OCRFormat';
import '../../styling/OCRViewer.css';

export default function ImgViewer({ imgUrl, lang = 'eng', externalZoom }) {
  // ✅ Derive from prop so it updates whenever imgUrl changes
  const images = useMemo(() => {
    if (!imgUrl) return [];
    const name = imgUrl.split('/').pop() || 'image';
    return [{ url: imgUrl, name }];
  }, [imgUrl]);

  const active = 0;
  const activeImg = images[active] || null;

  // Fit when zoom==1, otherwise free (external control)
  const [fitToScreen, setFitToScreen] = useState(true);
  const [zoom, setZoom] = useState(1);
  const frameRef = useRef(null);

  // OCR bits
  const [ocrScale, setOcrScale] = useState(3.0);
  const [threshold, setThreshold] = useState(180);
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState({ index: 0, total: 0, percent: 0 });
  const [rawText, setRawText] = useState('');
  const [mdText, setMdText] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [showTab, setShowTab] = useState('txt');
  const [error, setError] = useState(null);

  const workCanvasRef = useRef(null);

  // ✅ Reset on image change
  useEffect(() => {
    setError(null);
    setRawText('');
    setMdText('');
    setJsonText('');
    setShowTab('txt');
    setProgress({ index: 0, total: images.length, percent: 0 });
  }, [imgUrl, images.length]);

  // 🔗 External zoom drives the view
  useEffect(() => {
    if (typeof externalZoom === 'number') {
      setZoom(externalZoom);
      setFitToScreen(externalZoom === 1);
    }
  }, [externalZoom]);

  // Drag-to-pan when not in Fit mode
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    let dragging = false;
    let sx = 0,
      sy = 0,
      sl = 0,
      st = 0;

    const down = (e) => {
      if (fitToScreen) return;
      // left mouse only
      if (e.button !== 0) return;

      dragging = true;
      sx = e.clientX;
      sy = e.clientY;
      sl = el.scrollLeft;
      st = el.scrollTop;
      el.classList.add('grabbing');
      el.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const move = (e) => {
      if (!dragging) return;
      el.scrollLeft = sl - (e.clientX - sx);
      el.scrollTop = st - (e.clientY - sy);
    };

    const up = () => {
      dragging = false;
      el.classList.remove('grabbing');
      el.style.cursor = '';
    };

    el.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);

    return () => {
      el.removeEventListener('mousedown', down);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [fitToScreen]);

  // === OCR helpers ===
  const renderImageToCanvas = (imgEl, scale) => {
    const srcW = imgEl.naturalWidth || imgEl.width;
    const srcH = imgEl.naturalHeight || imgEl.height;

    const w = Math.max(1, Math.floor(srcW * scale));
    const h = Math.max(1, Math.floor(srcH * scale));

    if (!workCanvasRef.current) workCanvasRef.current = document.createElement('canvas');
    const canvas = workCanvasRef.current;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;

    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(imgEl, 0, 0, w, h);

    return canvas;
  };

  const loadImageForOcr = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();

      // ✅ Important for OCR canvas usage:
      // If the image server does NOT send proper CORS headers, this will error.
      img.crossOrigin = 'anonymous';

      img.onload = () => resolve(img);
      img.onerror = (ev) => {
        reject(
          new Error(
            `Failed to load image for OCR. This is often a CORS issue or a bad/expired URL.\nURL: ${url}`
          )
        );
      };

      // Cache-bust in case you’re dealing with transient signed URLs or stale cache
      const cacheBusted = url.includes('?') ? `${url}&_cb=${Date.now()}` : `${url}?_cb=${Date.now()}`;
      img.src = cacheBusted;
    });

  const runOCR = useCallback(async () => {
    if (!images.length) return;

    setIsExtracting(true);
    setError(null);
    setRawText('');
    setMdText('');
    setJsonText('');
    setShowTab('txt');

    try {
      const rawOut = [];
      const mdOut = [];
      const jsonOut = [];

      for (let i = 0; i < images.length; i++) {
        setProgress({ index: i + 1, total: images.length, percent: 0 });

        const meta = images[i];

        const imgEl = await loadImageForOcr(meta.url);

        const auto = recommendOcrScale(imgEl);
        const useScale = Number.isFinite(+ocrScale) ? +ocrScale : auto;

        const canvas = renderImageToCanvas(imgEl, useScale);
        const cleaned = preprocessCanvas(canvas, Number.isFinite(+threshold) ? +threshold : null);
        const dataUrl = cleaned.toDataURL('image/png');

        const result = await Tesseract.recognize(dataUrl, lang, {
          logger: (m) => {
            if (m.status === 'recognizing text' && m.progress != null) {
              setProgress((p) => ({ ...p, percent: Math.round(m.progress * 100) }));
            }
          }
        });

        const pageRaw = (result.data?.text || '').replace(/\r\n?/g, '\n');

        rawOut.push(
          `\n\n=== Image ${i + 1}/${images.length} — ${meta.name} ===\n\n${pageRaw.trim()}`
        );

        const struct = toStructured(result.data);
        mdOut.push(`\n\n## Image ${i + 1}: ${meta.name}\n\n${renderMarkdown(struct)}`);
        jsonOut.push({ index: i + 1, name: meta.name, struct });
      }

      setRawText(rawOut.join('\n').trim());
      setMdText(mdOut.join('\n').trim());
      setJsonText(JSON.stringify({ images: jsonOut }, null, 2));
      setProgress((p) => ({ ...p, percent: 100 }));
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Error extracting text from image(s).');
      setProgress((p) => ({ ...p, percent: 0 }));
    } finally {
      setIsExtracting(false);
    }
  }, [images, lang, ocrScale, threshold]);

  const copy = (s) => navigator.clipboard.writeText(s);

  const downloadText = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="img-container">
      <div className="pdf-pane">
        {/* Zoomable frame (no internal zoom UI) */}
        <div className={`img-frame ${fitToScreen ? 'fit' : 'free'}`} ref={frameRef}>
          {activeImg ? (
            <img
              // ✅ Remount img when URL changes
              key={activeImg.url}
              src={activeImg.url}
              alt={activeImg.name}
              className="view-image"
              style={
                fitToScreen
                  ? {} // centered & contained (CSS)
                  : { width: `${zoom * 100}%`, height: 'auto', margin: 0 } // anchor top-left
              }
              onError={(e) => {
                // ✅ Don’t rely on inspecting the pooled event later; log the URL immediately
                const src = e?.currentTarget?.src || e?.target?.src || activeImg.url;
                console.error('IMG display failed:', src);
                setError(`Failed to load image: ${src}`);
              }}
            />
          ) : (
            <div className="placeholder">No image</div>
          )}
        </div>
      </div>

      {/* OCR controls */}
      <div className="ocr-pane">
        <div className="ocr-controls">
          <label>
            OCR language <input value={lang} readOnly />
          </label>

          <label>
            OCR render scale
            <input
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              value={ocrScale}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setOcrScale(Number.isFinite(v) ? v : '');
              }}
            />
          </label>

          <button
            type="button"
            onClick={() => {
              if (!activeImg) return;
              const probe = new Image();
              probe.onload = () => setOcrScale(recommendOcrScale(probe));
              // (Auto here doesn’t need crossOrigin since we only read dimensions)
              probe.src = activeImg.url;
            }}
            disabled={!activeImg}
          >
            Auto (≈300 DPI)
          </button>

          <label>
            Threshold (0–255 or blank)
            <input
              type="number"
              min="0"
              max="255"
              placeholder="e.g. 180"
              value={threshold}
              onChange={(e) => {
                const v = e.target.value;
                setThreshold(v === '' ? '' : Math.max(0, Math.min(255, +v || 0)));
              }}
            />
          </label>

          <button type="button" onClick={runOCR} disabled={isExtracting || !images.length}>
            {isExtracting ? 'Extracting…' : 'Extract Text'}
          </button>
        </div>

        {isExtracting && (
          <div className="ocr-progress">
            Image {progress.index}/{progress.total} • {progress.percent}%
          </div>
        )}
        {error && <div style={{ color: '#dc2626', marginTop: 8 }}>{error}</div>}

        {/* Tabs for outputs */}
        {(rawText || mdText || jsonText) && (
          <div className="ocr-output-wrap">
            <div className="ocr-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowTab('txt')}
                className={showTab === 'txt' ? 'active' : ''}
              >
                Text
              </button>
              <button
                type="button"
                onClick={() => setShowTab('md')}
                className={showTab === 'md' ? 'active' : ''}
              >
                Markdown
              </button>
              <button
                type="button"
                onClick={() => setShowTab('json')}
                className={showTab === 'json' ? 'active' : ''}
              >
                JSON
              </button>

              <div style={{ flex: 1 }} />

              {showTab === 'txt' && rawText && (
                <>
                  <button type="button" onClick={() => copy(rawText)}>
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadText(rawText, 'image_ocr_text.txt')}
                  >
                    Download .txt
                  </button>
                </>
              )}

              {showTab === 'md' && mdText && (
                <>
                  <button type="button" onClick={() => copy(mdText)}>
                    Copy
                  </button>
                  <button type="button" onClick={() => downloadText(mdText, 'image_ocr.md')}>
                    Download .md
                  </button>
                </>
              )}

              {showTab === 'json' && jsonText && (
                <>
                  <button type="button" onClick={() => copy(jsonText)}>
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadText(jsonText, 'image_ocr.json')}
                  >
                    Download .json
                  </button>
                </>
              )}
            </div>

            {showTab === 'txt' && <textarea className="ocr-output" rows={20} readOnly value={rawText} />}
            {showTab === 'md' && <textarea className="ocr-output" rows={20} readOnly value={mdText} />}
            {showTab === 'json' && <textarea className="ocr-output" rows={20} readOnly value={jsonText} />}
          </div>
        )}
      </div>
    </div>
  );
}
